import React from 'react';
import { Table, Tag, Space, Button, Avatar, Tooltip, Select } from 'antd';
import {
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  CopyOutlined,
  PushpinOutlined,
  PushpinFilled,
} from '@ant-design/icons';
import { toJalali } from '../../../utils/dateUtils';

const priorityMeta = {
  high: { color: 'red', text: 'بالا' },
  medium: { color: 'blue', text: 'متوسط' },
  low: { color: 'default', text: 'پایین' },
};

const formatTaskDate = (value) => {
  if (!value) return '—';
  const jalali = toJalali(value);
  return jalali || value;
};

const getTaskLabels = (row) => {
  const raw = row.labels || row.task_labels || row.taskLabels || [];
  if (!Array.isArray(raw)) return [];
  return raw
    .map((l) => ({
      id: l.id || l.label_id,
      name: l.label_name || l.name || l.title,
      color: l.color || l.label_color,
    }))
    .filter((l) => l.name);
};

/** نگاشت وضعیت انگلیسی/اسلاگ به ستون فارسی */
const resolveStatusColumn = (row, statusOptions = []) => {
  const byId = (id) => statusOptions.find((c) => String(c.id) === String(id));

  if (row.board_column?.column_name) {
    return {
      id: row.board_column.id ?? row.board_column_id,
      name: row.board_column.column_name,
      color: row.board_column.label_color || row.board_column.color,
    };
  }

  if (row.board_column_id != null) {
    const col = byId(row.board_column_id);
    if (col) {
      return {
        id: col.id,
        name: col.column_name || col.name,
        color: col.label_color || col.color,
      };
    }
  }

  const statusRaw = row.status || row.board_column?.slug;
  if (statusRaw != null && statusRaw !== '') {
    const col =
      byId(statusRaw) ||
      statusOptions.find(
        (c) =>
          String(c.slug) === String(statusRaw) ||
          String(c.column_name) === String(statusRaw) ||
          String(c.name) === String(statusRaw),
      );
    if (col) {
      return {
        id: col.id,
        name: col.column_name || col.name,
        color: col.label_color || col.color,
      };
    }
  }

  return null;
};

const TasksTable = ({
  data = [],
  loading,
  pagination,
  onChange,
  onView,
  onEdit,
  onDelete,
  onDuplicate,
  onPin,
  onStatusChange,
  statusOptions = [],
  statusUpdatingId = null,
  pinUpdatingId = null,
  canEditRecord,
  canDeleteRecord,
  rowSelection,
}) => {
  const columns = [
    {
      title: 'کد',
      dataIndex: 'task_short_code',
      width: 100,
      render: (t) => <span className="font-mono text-xs" style={{ fontFamily: 'CustomFont' }}>{t || '—'}</span>,
    },
    {
      title: 'عنوان',
      dataIndex: 'heading',
      ellipsis: true,
      render: (t, row) => (
        <button
          type="button"
          className="text-indigo-600 dark:text-indigo-400 font-medium text-right bg-transparent border-0 p-0 cursor-pointer hover:underline"
          style={{ fontFamily: 'CustomFont' }}
          onClick={() => onView?.(row.id, row)}
        >
          {row.pinned || row.pinned_task ? (
            <PushpinFilled className="text-amber-500 ml-1" />
          ) : null}
          {t}
        </button>
      ),
    },
    {
      title: 'پروژه',
      dataIndex: 'project_name',
      width: 140,
      ellipsis: true,
      responsive: ['md'],
      render: (t) => <span style={{ fontFamily: 'CustomFont' }}>{t || '—'}</span>,
    },
    {
      title: 'وضعیت',
      key: 'status',
      width: 160,
      render: (_, row) => {
        const resolved = resolveStatusColumn(row, statusOptions);
        if (onStatusChange && statusOptions.length) {
          const current = resolved?.id ?? row.board_column_id ?? undefined;
          return (
            <Select
              size="small"
              value={current}
              placeholder="وضعیت"
              style={{ width: '100%', fontFamily: 'CustomFont', minWidth: 120 }}
              loading={statusUpdatingId === row.id}
              disabled={canEditRecord && !canEditRecord(row)}
              onChange={(val) => onStatusChange(row, val)}
              optionLabelProp="label"
              options={statusOptions.map((c) => ({
                value: c.id,
                label: c.column_name || c.name,
              }))}
            />
          );
        }
        return (
          <Tag color={resolved?.color || row.label_color || 'default'} style={{ fontFamily: 'CustomFont' }}>
            {resolved?.name || '—'}
          </Tag>
        );
      },
    },
    {
      title: 'اولویت',
      dataIndex: 'priority',
      width: 90,
      responsive: ['sm'],
      render: (p) => {
        const m = priorityMeta[p] || { color: 'default', text: p || '—' };
        return <Tag color={m.color} style={{ fontFamily: 'CustomFont' }}>{m.text}</Tag>;
      },
    },
    {
      title: 'تاریخ شروع',
      dataIndex: 'start_date',
      width: 110,
      responsive: ['lg'],
      render: (v, row) => (
        <span style={{ fontFamily: 'CustomFont' }}>
          {formatTaskDate(v || row.start_on)}
        </span>
      ),
    },
    {
      title: 'سررسید',
      dataIndex: 'due_date',
      width: 110,
      responsive: ['lg'],
      render: (v, row) => (
        <span style={{ fontFamily: 'CustomFont' }}>
          {formatTaskDate(v || row.due_date || row.due_on)}
        </span>
      ),
    },
    {
      title: 'تگ‌ها',
      key: 'labels',
      width: 160,
      responsive: ['xl'],
      render: (_, row) => {
        const labels = getTaskLabels(row);
        if (!labels.length) return <span style={{ fontFamily: 'CustomFont' }}>—</span>;
        return (
          <Space size={[4, 4]} wrap>
            {labels.slice(0, 3).map((l) => (
              <Tag key={l.id || l.name} color={l.color || 'default'} style={{ fontFamily: 'CustomFont' }}>
                {l.name}
              </Tag>
            ))}
            {labels.length > 3 ? (
              <Tag style={{ fontFamily: 'CustomFont' }}>+{labels.length - 3}</Tag>
            ) : null}
          </Space>
        );
      },
    },
    {
      title: 'مسئول',
      key: 'users',
      width: 100,
      responsive: ['lg'],
      render: (_, row) => (
        <Avatar.Group maxCount={2} size="small">
          {(row.users || []).map((u) => (
            <Tooltip key={u.id} title={u.name}>
              <Avatar src={u.image_url || u.image}>{u.name?.[0]}</Avatar>
            </Tooltip>
          ))}
        </Avatar.Group>
      ),
    },
    {
      title: 'عملیات',
      key: 'actions',
      width: 180,
      fixed: 'right',
      render: (_, row) => {
        const isPinned = Boolean(row.pinned || row.pinned_task);
        return (
          <Space size="small">
            <Tooltip title="مشاهده">
              <Button type="text" icon={<EyeOutlined />} onClick={() => onView?.(row.id, row)} />
            </Tooltip>
            {(!canEditRecord || canEditRecord(row)) && onEdit ? (
              <Tooltip title="ویرایش">
                <Button type="text" icon={<EditOutlined />} onClick={() => onEdit(row.id)} />
              </Tooltip>
            ) : null}
            {onDuplicate ? (
              <Tooltip title="ایجاد نسخه مشابه">
                <Button type="text" icon={<CopyOutlined />} onClick={() => onDuplicate(row)} />
              </Tooltip>
            ) : null}
            {onPin ? (
              <Tooltip title={isPinned ? 'برداشتن سنجاق' : 'سنجاق کردن'}>
                <Button
                  type="text"
                  icon={isPinned ? <PushpinFilled className="text-amber-500" /> : <PushpinOutlined />}
                  loading={pinUpdatingId === row.id}
                  onClick={() => onPin(row)}
                />
              </Tooltip>
            ) : null}
            {(!canDeleteRecord || canDeleteRecord(row)) && onDelete ? (
              <Tooltip title="حذف">
                <Button type="text" danger icon={<DeleteOutlined />} onClick={() => onDelete(row.id)} />
              </Tooltip>
            ) : null}
          </Space>
        );
      },
    },
  ];

  return (
    <Table
      rowKey="id"
      columns={columns}
      dataSource={data}
      loading={loading}
      scroll={{ x: 1100 }}
      pagination={pagination}
      onChange={onChange}
      rowSelection={rowSelection}
      size="middle"
    />
  );
};

export default TasksTable;
